import { DatePipe } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseFormControlComponent } from '../base-form-control.component';
import { DateFormat } from '../../../constants/date-format.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { DomEvents } from '../../../constants/dom-events.constants';

@Component({
    selector: 'lsq-date-input',
    template: `
        <div class="lsq-date-input">
            <lsq-label
                *ngIf="label"
                [label]="label"
                [subLabel]="subLabel"
                [style.marginBottom]="'1rem'"
            ></lsq-label>
            <div
                class="lsq-form-control"
                [class.focused]="isFocused"
                [class.has-error]="showErrorMessage(shouldValidate())"
                (click)="handleFocus()"
            >
                <mat-form-field
                    class="input-container"
                    [ngClass]="{ 'date-placeholder': !valueString && !isFocused }"
                >
                    <input
                        matInput
                        #inputElement
                        max="9999-12-31"
                        [id]="name"
                        [class.has-float-label]="!!label"
                        [type]="'date'"
                        [value]="valueString"
                        [ngModel]="valueString"
                        [ngModelOptions]="ngModelOptions"
                        [disabled]="disabled"
                        (focus)="setInputFocus(true)"
                        (blur)="handleInputBlur()"
                        (keypress)="handleInternalKeyPress($event)"
                        (ngModelChange)="handleModelChange($event)"
                    />
                </mat-form-field>
            </div>
            <h6
                class="input-error-message text-uppercase font-weight-bold"
                *ngIf="showErrorMessage(shouldValidate())"
            >
                {{ getErrorMessage() }}
            </h6>
        </div>
    `,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['../common-styles/lsq-common-form-control.scss', './date-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateInputComponent),
            multi: true
        }
    ]
})
export class DateInputComponent extends BaseFormControlComponent<any>
    implements OnInit, ControlValueAccessor, OnChanges {
    @ViewChild('inputElement') public inputElement: ElementRef;

    @Input() public name!: string;
    @Input() public valueString!: string;
    @Input() public label?: string;
    @Input() public subLabel?: string;
    @Input() public allowFuture = true;
    @Input() public allowPast = true;
    @Input() public customDateFilter?: (d: Date | null) => boolean;
    @Input() public hasError = false;

    @Output() public blurred = new EventEmitter<undefined>();
    @Output() public valueChanged = new EventEmitter<Date>();

    public readonly ngModelOptions = { updateOn: DomEvents.Change };
    public isFocused = false;
    public hasInitialBlurHappened = false;

    constructor(public injector: Injector, private datePipe: DatePipe) {
        super(injector);
    }

    public ngOnInit(): void {
        if (this.group?.get(this.formControlName)?.value) {
            this.handleChange(this.group.get(this.formControlName).value);
        }
        this.valueString = this.datePipe.transform(this.model, DateFormat.ISO8601) || '';
        this.modelChangeSubject.subscribe((value: Date) => {
            this.valueString = this.datePipe.transform(value, DateFormat.ISO8601) || '';
        });
    }
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.value) {
            var newValue =
                this.datePipe.transform(changes.value.currentValue, DateFormat.ISO8601) || '';
            if (this.valueString === newValue) {
                //This makes sure the DOM is kept updated if the value has not changed.
                this.inputElement.nativeElement.value = newValue;
            } else {
                this.valueString = newValue;
            }
            this.handleChange(changes.value.currentValue);
        }
    }

    public handleFocus(): void {
        this.isFocused = true;
    }

    public handleModelChange(value): void {
        var newValue = !!value ? new Date(Date.parse(value)) : null;
        if (newValue) {
            newValue = new Date(
                newValue.getUTCFullYear(),
                newValue.getUTCMonth(),
                newValue.getUTCDate()
            );
        }
        this.model = newValue;
        this.valueChanged.emit(this.model);
        this.handleChange(newValue);
    }

    public setInputFocus(focused: boolean): void {
        this.isFocused = focused;
        if (!focused) {
            this.handleInputBlur();
        }
    }

    public handleInputBlur(): void {
        this.blurred.emit();
        this.hasInitialBlurHappened = true;
        this.isFocused = false;
        this.valueString = this.datePipe.transform(this.model, DateFormat.ISO8601) || '';
        //This makes sure the DOM is kept updated if the value has not changed.
        this.inputElement.nativeElement.value = this.valueString;
    }

    public handleInternalKeyPress(event: KeyboardEvent): void {
        if (event.which === KeyCodes.Enter) {
            // If the user presses 'enter' force a blur for validation.
            this.blurred.emit();
            return;
        }
        // Only allow number keys.
        if (event.which < KeyCodes.Zero || event.which > KeyCodes.Nine) {
            event.preventDefault();
            return;
        }
    }

    public shouldValidate(): boolean {
        return !this.isFocused || this.hasInitialBlurHappened;
    }
}
